import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SelectionItem } from '../select/select.component';
import { LanguageService } from '../../service/language.service';
import { AbstractControl, FormControl } from '@angular/forms';
import { ChecklistValidationErrors } from '../../../round-planning/service/utility/checklist-validation-service';
import { FormControlEx } from '../forms/form-control';
import { Subscription } from 'rxjs';

interface Week {
  weekNumber: number;
  startDateString: string;
  endDateString: string;
  checked: boolean;
  lastDayNumber: number;
}

export function parseIntegersString(numbers: string): number[] {
  // TODO: move somewhere better?
  return numbers
    .split(/\D+/g)
    .filter(x => x !== '')
    .map(x => parseInt(x));
}

@Component({
    selector: 'app-date-week-picker',
    templateUrl: './date-week-picker.component.html',
    styleUrls: ['./date-week-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WeekPickerComponent implements OnInit, OnDestroy {
  constructor(private _languageService: LanguageService) {}

  @Input() control: AbstractControl;
  @Input() isReadonly: boolean = false;

  weeks: Week[];
  year: number;
  showDropdown: boolean = false;

  private _yearLimitLow: number = 2024;
  private _yearLimitHigh: number = 2100;
  private _averageWeekNumber: number = 52;
  private _maxWeekNumber: number = 53;
  private _decemberWeekLimitDate: number = 28;

  customSelectWeekInterval: number;
  customWeekIntervals: SelectionItem<number>[];
  customWeekIntervalStart: SelectionItem<number>[];
  startWeek: number;
  customWeekIntervalEnd: SelectionItem<number>[];
  endWeek: number;
  selectedWeeksString: string = '';
  readonly selectedWeeksControl = new FormControlEx<string>('ROUND_PLANNING_CHECKLIST_FORM.WEEKS_INPUT', '');
  private readonly subscriptions = new Subscription();

  get showNextYear() {
    return this.year < this._yearLimitHigh;
  }

  get showPreviousYear() {
    return this.year > this._yearLimitLow;
  }

  get selectedWeeks(): number[] {
    return this.control.value || [];
  }

  get selectedWeeksEmpty() {
    return this.selectedWeeksString.length === 0;
  }

  get errorMessage() {
    if (!this.control || !this.control.errors) {
      return null; // No control or no errors
    }

    if (this.control.errors[ChecklistValidationErrors.INTERVALS_EMPTY]) {
      return this.control.errors[ChecklistValidationErrors.INTERVALS_EMPTY].message;
    }

    if (this.control.errors[ChecklistValidationErrors.INVALID_INTERVALS]) {
      return this.control.errors[ChecklistValidationErrors.INVALID_INTERVALS].message;
    }

    if (this.control.errors[ChecklistValidationErrors.DUPLICATE_INTERVALS]) {
      return this.control.errors[ChecklistValidationErrors.DUPLICATE_INTERVALS].message;
    }

    return null;
  }

  ngOnInit() {
    this.year = new Date().getFullYear();
    this._getWeeksInYear(this.year);

    const emptyInterval = new SelectionItem<number>();
    Object.assign(emptyInterval, {
      name: '-',
      value: 0,
    });

    const weekIntervals = [emptyInterval];
    for (let i = 1; i < 13; i++) {
      weekIntervals.push({
        name: i.toString(),
        value: i,
      });
    }

    const weekIntervalsStart = [emptyInterval];
    const weekIntervalsEnd = [emptyInterval];
    for (let i = 1; i <= this._maxWeekNumber; i++) {
      weekIntervalsStart.push({ name: i.toString(), value: i });
      weekIntervalsEnd.push({ name: i.toString(), value: i });
    }

    this.customWeekIntervals = weekIntervals;
    this.customWeekIntervalStart = weekIntervalsStart;
    this.customWeekIntervalEnd = weekIntervalsEnd;

    this._setSelectedWeeksString(this.selectedWeeks.length > 0 ? this.selectedWeeks.join(' ') + ' ' : '');
    this.subscriptions.add(this.selectedWeeksControl.valueChanges.subscribe(value => this.selectWeeksChange(value ?? '')));
    this.subscriptions.add(this.control.statusChanges.subscribe(() => this._syncValidationState()));
    this.subscriptions.add((this.control as FormControlEx)?.touchStateChanged?.subscribe(() => this._syncValidationState()));
    this._syncValidationState();

    this._refreshIntervalSelectorValues();
  }

  weekItemActive(weekNumber: number): boolean {
    return this.selectedWeeksString.split(' ').includes(weekNumber.toString());
  }

  uncheckAll() {
    this._setSelectedWeeksString('');
    this._setControlValue([]);
  }

  checkWeeksCustom = (): boolean => {
    if (this.selectedWeeks.length === this.weeks.length) {
      this._setSelectedWeeksString('');
      this._setControlValue([]);
      return false;
    }

    const intervalSelectorsHaveValue = this.startWeek > 0 && this.endWeek > 0 && this.customSelectWeekInterval > 0;
    if (intervalSelectorsHaveValue) {
      const newSelect = [];
      for (let i = this.startWeek; i <= this.endWeek; i += this.customSelectWeekInterval) {
        newSelect.push(i);
      }
      this._setSelectedWeeksString(newSelect.join(' '));
      this._setControlValue(newSelect);
      return false;
    }

    const newSelect = this.weeks.map((_, index) => index + 1);
    this._setSelectedWeeksString(newSelect.join(' '));
    this._setControlValue(newSelect);

    return true;
  };

  onSelectCustomInterval(weekInterval: number | number[]) {
    this.customSelectWeekInterval = Array.isArray(weekInterval) ? weekInterval[0] : weekInterval;
  }

  onSelectStartInterval(startWeek: number | number[]) {
    this.startWeek = Array.isArray(startWeek) ? startWeek[0] : startWeek;
    if (this.startWeek === this.customWeekIntervalStart[0].value) {
      this._refreshIntervalSelectorValues();
      return;
    }

    if (this.endWeek < this.startWeek) {
      this.endWeek = this._maxWeekNumber;
    }

    this._updateEndWeekOptions();
  }

  onSelectEndInterval(endWeek: number | number[]) {
    this.endWeek = Array.isArray(endWeek) ? endWeek[0] : endWeek;
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  changeYear(isIncrement: boolean) {
    if (this.isReadonly) {
      return;
    }

    this.year += isIncrement ? 1 : -1;
    this._getWeeksInYear(this.year);
  }

  selectWeeksChange(selectedWeeksString: string) {
    const weeks = this._parseWeeks(selectedWeeksString);
    // Preserve separators while typing so values such as "1 4 12" remain practical to enter.
    this.selectedWeeksString = selectedWeeksString;
    this._setControlValue(weeks);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectWeekNumberClick(number: number) {
    if (this.isReadonly) {
      return;
    }

    const weeks = this._parseWeeks();
    const index = weeks.indexOf(number);
    if (index !== -1) {
      weeks.splice(index, 1);
    } else {
      weeks.push(number);
    }

    this._setSelectedWeeksString(weeks.length == 0 ? '' : weeks.join(' ') + ' ');
    this._setControlValue(weeks);
    this._refreshIntervalSelectorValues();
  }

  private _getWeeksInYear(year: number) {
    this.weeks = Array.from({ length: this._averageWeekNumber }, (_, index) => this._createWeekItem(year, index + 1));

    if (this.weeks.length === this._averageWeekNumber && this.weeks[this.weeks.length - 1].lastDayNumber < this._decemberWeekLimitDate) {
      this.weeks.push(this._createWeekItem(year, this._maxWeekNumber));
    }
  }

  private _createWeekItem(year: number, weekNumber: number): Week {
    const firstDate = this._getWeekDate(year, weekNumber);
    const lastDate = new Date(this._getWeekDate(year, weekNumber + 1).getTime() - 1);

    return {
      weekNumber,
      startDateString: this._formatDateAsDayMonth(firstDate),
      endDateString: this._formatDateAsDayMonth(lastDate),
      checked: false,
      lastDayNumber: lastDate.getDate(),
    };
  }

  private _parseWeeks(selectedWeeksString?: string): number[] {
    return parseIntegersString(selectedWeeksString ?? this.selectedWeeksString);
  }

  private _getWeekDate(year: number, week: number, dayOfWeekISO: number = 1) {
    const jan4 = new Date(year, 0, 4);
    const day = week * 7 + dayOfWeekISO - 3 - (jan4.getDay() || 7);
    return new Date(year, 0, day);
  }

  private _formatDateAsDayMonth(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const formattedDate = date.toLocaleString(this._languageService.currentCultureLang, options);
    const [day, month] = formattedDate.split(' ');

    return `${day}, ${month}`;
  }

  private _refreshIntervalSelectorValues() {
    this.startWeek = this.customWeekIntervalStart[0].value;
    this.endWeek = this.customWeekIntervalEnd[0].value;
    this.customSelectWeekInterval = this.customWeekIntervals[0].value;
  }

  private _setSelectedWeeksString(value: string): void {
    this.selectedWeeksString = value;
    this.selectedWeeksControl.setValue(value, { emitEvent: false });
  }

  private _syncValidationState(): void {
    // The editable Astral field mirrors the scheduling control, so it must also
    // receive its validation state and interaction state.
    this.selectedWeeksControl.setErrors(this.control.errors);
    if (this.control.touched) {
      this.selectedWeeksControl.markAsTouched();
    }
    if (this.control.dirty) {
      this.selectedWeeksControl.markAsDirty();
    }
  }

  private _updateEndWeekOptions() {
    this.customWeekIntervalEnd.forEach((item, index) => (index < this.startWeek ? item.setDisabled(true) : item.setDisabled(false)));
  }

  private _setControlValue(value: number[]) {
    this.control.setValue(value);
    this.control.markAsTouched();
    this.control.markAsDirty();
  }
}
